/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    KanbanComponent,
    ColumnsDirective,
    ColumnDirective,
} from "@syncfusion/ej2-react-kanban";
import { addClass } from "@syncfusion/ej2-base";
import SaveTasks from "../../../services/general/TaskCrud.api";
import { useParams } from "react-router-dom";
import UpdateTask from "./update-task";
import DeleteBoard from "./delete-board";
import { CButton } from "@coreui/react";
import { ArrowLeft, Calendar } from "lucide-react";
import { backgroundColor } from "@syncfusion/ej2-richtexteditor/src/rich-text-editor/models/items";
import ProviderCreateTask from "./providerCreateTask";
 import { Trash2 } from "lucide-react";
import UseIsMobile from '../../../components/shared/UseIsMobile'
import { useSidebar } from "../../../components/SidebarContext";
import ProviderUpdateTask from "./providerUpdateTask";
 import {  CModal,  CModalHeader,  CModalTitle,  CModalBody,  CModalFooter} from "@coreui/react";
import { propTypes } from "react-bootstrap/esm/Image";
import { findKanbanStatusColumn, normalizeKanbanColumns, toNumericStatusId } from "./kanbanStatusUtils";
import { formatValueToMMDDYYYY, parseUSDateValue } from "../../../components/shared/customDateFormatUtils";
import "../dashboard.css";
 
const ProviderKanban = () => {
    const { id: boardId } = useParams();
    const [boardData, setBoardData] = useState(null);
    const [createTaskVisible, setCreateTaskVisible] = useState(false);
    const [updateTaskVisible, setUpdateTaskVisible] = useState(false);
    const [updateTaskId, setUpdateTaskId] = useState(null);
    const [deleteTaskId, setDeleteTaskId] = useState(null);
    const [deleteVisible, setDeleteVisible] = useState(false)
    const [isDeletingTask, setIsDeletingTask] = useState(false)
    const [kanbanRenderKey, setKanbanRenderKey] = useState(0)
    const kanbanRef = useRef(null)
    const [draggedId, setDraggedId] = useState({
        id: "",
        statusId: ""
    });
      const isMobile = UseIsMobile()

    const normalizedColumns = useMemo(
        () => normalizeKanbanColumns(boardData?.columns || []),
        [boardData?.columns]
    );

    //   Prepare Kanban Tasks
    const kanbanTasks = useMemo(() => {
        if (!boardData?.tasks) return [];

        return boardData.tasks.map((task) => ({
            ...task,
            statusId:
                toNumericStatusId(task.statusId) ??
                findKanbanStatusColumn(normalizedColumns, task.statusId, task.status)?.keyField ??
                "",
            status:
                findKanbanStatusColumn(normalizedColumns, task.statusId, task.status)?.headerText ??
                task.status,
        }));
    }, [boardData?.tasks, normalizedColumns]);

    const providerKanbanMinWidth = Math.max((normalizedColumns?.length || 1) * 240, 600);

    const stopCardActionEvent = (event) => {
        event.stopPropagation();
        event.nativeEvent?.stopImmediatePropagation?.();
    };

    const restoreKanbanView = useCallback(() => {
        setBoardData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                tasks: (prev.tasks || []).map((task) => ({ ...task })),
            };
        });
        setKanbanRenderKey((prev) => prev + 1);
    }, []);

    const openDeleteConfirm = (task) => {
        if (!task) return;
        restoreKanbanView();
        setDeleteTaskId(task.id);
        setDeleteVisible(true);
    };

    const resolveDropStatusId = (args) => {
        const dataStatusId = args?.data?.[0]?.statusId;
        if (dataStatusId !== null && dataStatusId !== undefined && dataStatusId !== "") {
            return dataStatusId;
        }

        const target = args?.event?.target;
        const targetColumn = target?.closest?.(".e-content-cells[data-key], .e-header-cells[data-key], [data-role='kanban-column'][data-key]");
        return targetColumn?.getAttribute?.("data-key") || "";
    };

    const resetDeleteConfirm = () => {
        setDeleteVisible(false);
        setDeleteTaskId(null);
        setIsDeletingTask(false);
    };

    const closeDeleteConfirm = () => {
        resetDeleteConfirm();
        restoreKanbanView();
    };

    const handleDelete = async () => {
        if (deleteTaskId === null || deleteTaskId === undefined || isDeletingTask) return;
        setIsDeletingTask(true);
        const deleted = await deleteTask(deleteTaskId);
        if (deleted) {
            resetDeleteConfirm();
        } else {
            setIsDeletingTask(false);
        }
    }


    //   Card Template
    const cardTemplate = (props) => {
        const assignees = Array.isArray(props.Assignee) ? props.Assignee : Array.isArray(props.assignee) ? props.assignee : []
        const assigneeText = assignees.map((a) => a.value || a.label).join(', ')

        const linkedMedication = Array.isArray(props.linkedMedications) ? props.linkedMedications[0] : props.linkedMedications
        const linkedPatientName = String(linkedMedication?.patientName || linkedMedication?.PatientName || '').trim()
        const linkedMedicationName = String(linkedMedication?.name || linkedMedication?.medicationName || props.medicationName || props.MedicationName || '').trim()
        const rawTitle = props.title || props.Title || ''
        const title =
            linkedPatientName && linkedMedicationName
                ? `${linkedPatientName} - ${linkedMedicationName}`
                : rawTitle
        const dueRaw = props.dueDate || props.DueDate || props.Due || props.due || null
        const dueDate = parseUSDateValue(dueRaw)
        const formattedDue = formatValueToMMDDYYYY(dueRaw)
        const priority = props.priority || props.Priority || ''
        const status = props.status || props.Status || props.StatusId || ''

        const priorityColor =
            priority === 'High'
                ? 'rgb(154 18 18)'
                : priority === 'Low'
                    ? '#16a34a'
                    : '#2563eb'

        return (
            <div
                className="p-2 rounded-lg shadow-lg"
                style={{ background: '#191919', position: 'relative', cursor: 'pointer' }}
                onClick={() => {
                    setUpdateTaskId(props)
                    setUpdateTaskVisible(true)
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                    <p className="p-0 m-0 text-white" style={{ fontWeight: 600, letterSpacing: '-0.4px', lineHeight: '20px', fontSize: '16px' }}>{title}</p>
                    {formattedDue && (
                        (() => {
                            const dueObj = dueDate ? new Date(dueDate) : null
                            const today = new Date(); today.setHours(0,0,0,0)
                            const dueCompare = dueObj ? new Date(dueObj) : null
                            if (dueCompare) dueCompare.setHours(0,0,0,0)
                            const overdue = dueCompare ? dueCompare < today : false
                            return (
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        border: `1px solid ${overdue ? 'rgb(199 22 22)' : 'var(--ui-dashboard-border-strong)'}`,
                                        borderRadius: 8,
                                        padding: '4px 8px',
                                        background: 'transparent',
                                        width: 'fit-content',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Calendar size={16} style={{ color: overdue ? 'rgb(199 22 22)' : 'var(--ui-text-muted)' }} />
                                    <div style={{ color: overdue ? 'rgb(199 22 22)' : 'var(--ui-text-muted)', fontSize: '16px', lineHeight: '20px' }}>{formattedDue}</div>
                                </div>
                            )
                        })()
                    )}
                    <div style={{ color: priorityColor, fontWeight: 700, fontSize: '16px', lineHeight: '20px' }}>{priority}</div>
                    {(() => {
                        const subtasks = props.subTasks || props.subtask || props.SubTasks || props.subtasks || []
                        const totalSub = Array.isArray(subtasks) ? subtasks.length : 0
                        const completedSub = Array.isArray(subtasks) ? subtasks.filter(s => s.completed || s.isCompleted || s.done || s.checked || s.Status === 'Done' || s.status === 'done').length : 0
                        return totalSub > 0 ? <div style={{ color: '#6b7280', fontSize: '16px', lineHeight: '20px' }}>{`${completedSub}/${totalSub}`}</div> : null
                    })()}
                </div>
            </div>
        )
    }

    const fetchBoard = async () => {
        const { data, error } = await supabase
            .from("boards")
            .select(`*, tasks (*)`)
            .eq("id", boardId)
            .single();

        if (error) {
            console.error("Fetch error:", error);
        } else {
            setBoardData(data);
        }
    };
    const deleteTask = async (id) => {
        const response = await SaveTasks('delete', id, null);
        const { error } = response || {}
        if (error) {
            console.error("Failed to delete task:", error);
            return false;
        }
        await fetchBoard(); // refresh after delete
        return true;
};
    //   Get Board Data
    useEffect(() => {
        if (!boardId) return;
        fetchBoard();
    }, [boardId]);

     const { closeSidebar } = useSidebar()
    
        useEffect(() => {
    
    
            const handleResize = () => {
                if (isMobile) {
                    closeSidebar()
                }
            }
    
            handleResize()
            window.addEventListener('resize', handleResize)
    
            return () => window.removeEventListener('resize', handleResize)
        }, [])

    return (
        <div className="task-board-theme dashboard-dark-theme p-4 min-vh-100">
            <div style={{ marginBottom: "10px", margin: "0px 12px" }} className="d-flex flex-column gap-2" >
                <p className="p-0 m-0" style={{ color: "var(--ui-text-primary)", fontWeight: 500, letterSpacing: "-0.4px", lineHeight: "20px", fontSize: "23px" }}>{boardData?.title}</p>
                <p className="p-0 m-0" style={{ color: "var(--ui-text-muted)", letterSpacing: "-0.4px", lineHeight: "20px", fontSize: "13px" }}>{boardData?.description}</p>
            </div>
            <div style={{ margin: "10px 12px" }} className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-2 justify-content-between align-items-center">

                    <CButton
                        variant="outline"
                        color="primary"
                        onClick={() => {
                            window.location.href = "/home/providerBacklog"
                        }}
                        className="task-board-icon-btn d-flex align-items-center justify-content-center"
                        style={{ minWidth: '45px', minHeight: '45px', width: '45px', height: '45px', padding: 0 }}
                    >
                        <ArrowLeft size={16} />
                    </CButton>
                    <ProviderCreateTask
                        visible={createTaskVisible}
                        fetchBoard={fetchBoard}
                        setVisible={setCreateTaskVisible}
                    />
                </div>
                <DeleteBoard fetchBoard={fetchBoard} boardData={boardData} />
            </div>
            <ProviderUpdateTask
                visible={updateTaskVisible}
                fetchBoard={fetchBoard}
                updateTaskId={updateTaskId}
                setUpdateTaskId={setUpdateTaskId}
                setVisible={setUpdateTaskVisible}
                boardData={boardData}
                breadcrumbPrevText="Provider Backlog"
                breadcrumbPrevHref="/home/providerBacklog"
            />
          <div className="kanban-mobile-scroll-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
              <div style={{ minWidth: `${providerKanbanMinWidth}px` }}>
               <KanbanComponent
                key={kanbanRenderKey}
                ref={kanbanRef}
                height="calc(100vh - 180px)"
                style={{ background: 'var(--ui-dashboard-bg)' }}
                keyField="statusId"
                dataBound={() => {

                    document
                        .querySelectorAll('.e-kanban .e-header-cells')
                        .forEach((el) => {
                            el.style.backgroundColor = 'var(--ui-dashboard-surface-2)'
                            el.style.borderBottom = '1px solid var(--ui-dashboard-border)'
                        })

                    document
                        .querySelectorAll('.e-kanban .e-header-text')
                        .forEach((text) => {
                            text.style.color = 'var(--ui-text-primary)'
                            text.style.fontWeight = '600'
                        })

                    document
                        .querySelectorAll('.e-kanban .e-item-count')
                        .forEach((count) => {
                            count.style.color = 'var(--ui-text-muted)'
                            count.style.backgroundColor = 'var(--ui-dashboard-bg)'
                            count.style.borderRadius = '12px'
                            count.style.padding = '2px 8px'
                            count.style.fontSize = '12px'
                            count.style.marginLeft = '6px'
                        })

                    document
                        .querySelectorAll('.e-kanban .e-content-cells')
                        .forEach((el) => {
                            el.style.backgroundColor = 'var(--ui-dashboard-surface)'
                        })
                }}
                dataSource={kanbanTasks}
                allowDragAndDrop={true}
                dragStart={(e) => {
                    const draggedTask = e.data?.[0];
                    setDraggedId({
                        id: draggedTask?.id || "",
                        statusId: toNumericStatusId(draggedTask?.statusId) ?? "",
                    });
                }}
                dragStop={async (e) => {
                    e.cancel = true;

                    if (!e.event?.target?.closest?.(".e-kanban")) {
                        restoreKanbanView();
                        setDraggedId({ id: "", statusId: "" });
                        return;
                    }

                    const draggedTask = e.data?.[0];
                    const originalTask = boardData?.tasks?.find((task) => String(task.id) === String(draggedTask?.id));
                    const newStatusColumn = findKanbanStatusColumn(
                        normalizedColumns,
                        resolveDropStatusId(e),
                        draggedTask?.status
                    );
                    const newStatusId = newStatusColumn?.keyField;
                    const newStatus = newStatusColumn?.headerText || draggedTask?.status || originalTask?.status || "";

                    if (newStatusId === null || newStatusId === undefined || !draggedTask?.id) {
                        restoreKanbanView();
                        setDraggedId({ id: "", statusId: "" });
                        return;
                    }

                    if (String(draggedId.statusId) === String(newStatusId)) {
                        restoreKanbanView();
                        setDraggedId({ id: "", statusId: "" });
                        return;
                    }

                    setBoardData((prev) => prev ? ({
                        ...prev,
                        tasks: prev.tasks.map((task) =>
                            task.id === draggedTask.id
                                ? { ...task, statusId: newStatusId, status: newStatus }
                                : task
                        ),
                    }) : prev);
                    setKanbanRenderKey((prev) => prev + 1);

                    const { data, error } = await supabase
                        .from("tasks")
                        .update({ statusId: newStatusId, status: newStatus })
                        .eq("id", draggedTask.id)
                        .select("id");

                    if (error || !data?.length) {
                        console.error("Failed to update task status:", error || `No task matched id ${draggedTask.id}`);
                        await fetchBoard();
                        setKanbanRenderKey((prev) => prev + 1);
                    }
                    setDraggedId({ id: "", statusId: "" });
                }}
                cardSettings={{
                    template: cardTemplate,
                    headerField: "id",
                }}
            >

                <ColumnsDirective >
                    {normalizedColumns.map((col) => {
                        return (
                            <ColumnDirective

                                key={col.keyField}
                                headerText={col.headerText}
                                keyField={col.keyField}
                                dataField={col.keyField}
                                allowToggle={false}
                            />
                        )
                    })}
                </ColumnsDirective>
            </KanbanComponent>
          </div>
         <CModal backdrop="static" visible={deleteVisible} onClose={closeDeleteConfirm} className="hrm-confirm-modal" centered>
        <CModalHeader>
          <CModalTitle>Delete Task</CModalTitle>
        </CModalHeader>
        <CModalBody>
          Are you sure you want to delete this task?
        </CModalBody>
        <CModalFooter>
          <CButton
            onClick={closeDeleteConfirm}
            variant="outline"
            color="secondary"
          >
            Cancel
          </CButton>
          <CButton className="hrm-modal-danger-btn" onClick={handleDelete} color="danger" disabled={isDeletingTask}>
            {isDeletingTask ? "Deleting..." : "Delete"}
          </CButton>
        </CModalFooter>
      </CModal>
        </div>
      </div>
    );
};

export default ProviderKanban;
